/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Confirm how a live session ends and apply the outcome.
 * @state-owner live UI store holds the end mode
 * @allowed-imports React; same-module store and mutations; dataset and workspace stores; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api live module
 * @side-effects ends the run, optionally opens a make-up homework run, and navigates
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { ModalDocument, OptionCard } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { findRun, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useEndRun } from '../api/live.mutations';
import { useLiveUiStore } from '../store/live-ui-store';

export function EndSessionDialog({ runId }: { runId: string }) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const endLiveSession = useEndRun();
  const endMode = useLiveUiStore((state) => state.endMode);
  const setEndMode = useLiveUiStore((state) => state.setEndMode);
  const run = findRun(runs, runId);
  if (!run) return null;

  /** and recalls the dispatch outside demo mode. */
  const confirm = () => {
    endLiveSession.mutate({ confirm: true, itemId: runId });
    const store = useDatasetStore.getState();
    store.updateRun(runId, { status: 'ended' });
    if (endMode === 'follow') {
      store.prependRun({
        id: store.allocateRunId(),
        activityId: run.activityId,
        classIndex: run.classIndex,
        mode: 'static',
        status: 'open',
        dateLabel: t('今天 補交'),
      });
    }
    closeModal();
    navigate('room', `c${run.classIndex}`, { classTab: 'rev' });
    notify(endMode === 'follow' ? '已結束，補交作業已開啟' : t('已結束，回顧報告產生中'));
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button className="bt pri" type="button" onClick={confirm}>
            {t('確認結束')}
          </button>
        </>
      }
      maxWidth={520}
      subtitle={runName(activities, run)}
      title={t('結束這堂')}
    >
      <OptionCard
        description={t('連結立刻失效，沒進來的學生就進不來了。')}
        on={endMode === 'only'}
        onSelect={() => setEndMode('only')}
        title={t('只結束')}
      />
      <OptionCard
        description={t('原連結失效，另外產生一份作業，開到今晚 23:59。')}
        on={endMode === 'follow'}
        onSelect={() => setEndMode('follow')}
        title={t('結束並開一份補交')}
      />
    </ModalDocument>
  );
}
